import React, { useMemo } from "react";
import { Box, Card, CardContent, Stack, Tooltip, Typography } from "@mui/material";
import { alpha, lighten, useTheme } from "@mui/material/styles";

import { AGREEMENT_STATUS } from "../../constants";
import { AGREEMENT_STATUS_LABELS } from "./agreementLabels";

// El ciclo de vida del convenio es una SECUENCIA (en estudio → en ejecución →
// finalizado → liquidado), no un conjunto de categorías intercambiables: cambiar
// el orden cambiaría el significado. Por eso se pinta con una rampa ORDINAL —un
// solo tono, de claro a oscuro— y no con cuatro colores distintos: así el orden
// se ve en el color.
//
// Los pasos se derivan del color del tema (REACT_APP_THEME) para que el módulo
// siga a la marca. Con el verde actual (#00652e) la rampa resultante
// —#84B59A, #5B9A77, #2E7D52, #00652E— pasa las cuatro comprobaciones de una
// rampa ordinal: luminosidad monótona, salto ΔL ≥ 0.06 entre pasos contiguos,
// extremo claro con contraste 2.26:1 sobre la superficie y un solo tono
// (dispersión 9°). Si se cambia el tema, revalidar esos coeficientes.
export const RAMP_STEPS = [0.52, 0.35, 0.17, 0];

export const STATUS_ORDER = [
  AGREEMENT_STATUS.UNDER_REVIEW,
  AGREEMENT_STATUS.IN_PROGRESS,
  AGREEMENT_STATUS.FINISHED,
  AGREEMENT_STATUS.SETTLED,
];

// El color sigue al ESTADO, no a su tamaño ni a su posición: filtrar u ordenar
// no puede repintar los que quedan.
export const useStatusRamp = () => {
  const { palette } = useTheme();
  return useMemo(
    () =>
      Object.fromEntries(
        STATUS_ORDER.map((s, i) => [s, lighten(palette.primary.main, RAMP_STEPS[i])])
      ),
    [palette.primary.main]
  );
};

// Leyenda compartida. Va siempre que haya más de una serie: la identidad de un
// tramo no puede depender sólo del color. Lleva la cifra, así que ningún dato
// queda escondido detrás de un hover.
const StatusLegend = ({ ramp, counts }) => (
  <Stack direction="row" flexWrap="wrap" sx={{ mt: 1.5, gap: 2 }}>
    {STATUS_ORDER.map((status) => (
      <Stack key={status} direction="row" alignItems="center" spacing={0.75}>
        <Box sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: ramp[status], flexShrink: 0 }} />
        <Typography variant="caption" color="text.secondary">
          {AGREEMENT_STATUS_LABELS[status]}
        </Typography>
        {counts && (
          <Typography variant="caption" sx={{ fontWeight: 600 }}>{counts[status] || 0}</Typography>
        )}
      </Stack>
    ))}
  </Stack>
);

const ChartCard = ({ title, subtitle, empty, children }) => (
  <Card variant="outlined" sx={{ height: "100%" }}>
    <CardContent>
      <Typography variant="subtitle2">{title}</Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
          {subtitle}
        </Typography>
      )}
      {empty ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Sin datos que mostrar.
        </Typography>
      ) : (
        children
      )}
    </CardContent>
  </Card>
);

// --- Convenios por vigencia --------------------------------------------------
// Columnas apiladas: cuántos convenios TERMINAN cada año, y en qué estado están.
// Parte-todo a lo largo del tiempo, que es lo que pide una columna apilada.
export function AgreementsByYearChart({ byEndYear = {}, onSelectStatus }) {
  const theme = useTheme();
  const ramp = useStatusRamp();

  const years = useMemo(() => {
    const rows = Object.entries(byEndYear)
      .map(([year, byStatus]) => ({
        year,
        byStatus: byStatus || {},
        total: STATUS_ORDER.reduce((acc, s) => acc + ((byStatus || {})[s] || 0), 0),
      }))
      .filter((r) => r.total > 0);
    rows.sort((a, b) => a.year.localeCompare(b.year));
    return rows;
  }, [byEndYear]);

  const max = Math.max(1, ...years.map((y) => y.total));
  const PLOT_HEIGHT = 150;
  const COLUMN_WIDTH = 56;

  return (
    <ChartCard
      title="Convenios por vigencia"
      subtitle="Año en que termina el convenio"
      empty={years.length === 0}
    >
      {/* Carriles de ancho FIJO, no flex: 1. Con dos años, repartir el ancho de
          la tarjeta dejaba las columnas a media pantalla una de otra. Así el
          gráfico crece hacia la derecha a medida que se acumulan vigencias y las
          columnas se comparan de un vistazo. */}
      <Box sx={{ display: "flex", alignItems: "flex-end", gap: 3, height: PLOT_HEIGHT, mt: 2, overflowX: "auto" }}>
        {years.map((row) => (
          <Stack key={row.year} sx={{ width: COLUMN_WIDTH, flexShrink: 0, height: "100%" }} justifyContent="flex-end" alignItems="center">
            {/* El valor va sobre la cabeza de la columna: una etiqueta por
                columna, no un número por segmento. */}
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
              {row.total}
            </Typography>
            <Box
              sx={{
                width: "100%",
                maxWidth: 24, // marca fina: la columna no llena su carril
                height: `${(row.total / max) * (PLOT_HEIGHT - 24)}px`,
                display: "flex",
                flexDirection: "column-reverse",
                // `gap` y no un margen: en column-reverse un margin-bottom cae
                // por debajo del primer tramo (contra la base) en vez de entre
                // los tramos. gap sólo separa lo que está en medio.
                gap: "2px",
              }}
            >
              {STATUS_ORDER.map((status, i) => {
                const value = row.byStatus[status] || 0;
                if (!value) return null;
                const isTop = !STATUS_ORDER.slice(i + 1).some((s) => row.byStatus[s]);
                return (
                  <Tooltip
                    key={status}
                    title={`${row.year} · ${AGREEMENT_STATUS_LABELS[status]}: ${value}`}
                  >
                    <Box
                      onClick={() => onSelectStatus?.(status)}
                      sx={{
                        flexGrow: value,
                        bgcolor: ramp[status],
                        cursor: onSelectStatus ? "pointer" : "default",
                        // Extremo redondeado arriba (el dato), recto abajo (la base).
                        borderTopLeftRadius: isTop ? 4 : 0,
                        borderTopRightRadius: isTop ? 4 : 0,
                      }}
                    />
                  </Tooltip>
                );
              })}
            </Box>
          </Stack>
        ))}
      </Box>
      {/* Eje: una línea fina de un paso sobre la superficie, nunca punteada. */}
      <Box sx={{ height: "1px", bgcolor: alpha(theme.palette.text.primary, 0.12), mt: 0.5 }} />
      {/* Mismo ancho y mismo hueco que los carriles de arriba: así cada año cae
          bajo su columna sin medir nada. */}
      <Box sx={{ display: "flex", gap: 3 }}>
        {years.map((row) => (
          <Typography
            key={row.year}
            variant="caption"
            color="text.secondary"
            sx={{ width: COLUMN_WIDTH, flexShrink: 0, textAlign: "center", mt: 0.5 }}
          >
            {row.year}
          </Typography>
        ))}
      </Box>
      {/* Sin cifras: sumadas darían exactamente las de "Convenios por estado",
          que están unos centímetros más abajo. Aquí la leyenda sólo tiene que
          decir qué color es cada estado. */}
      <StatusLegend ramp={ramp} />
    </ChartCard>
  );
}

// --- Convenios por entidad ---------------------------------------------------
// Barras horizontales porque los nombres de las entidades son largos. Las
// entidades son categorías NOMINALES —ninguna va antes que otra—, así que todas
// las barras llevan el MISMO tono: colorearlas por tamaño gastaría el canal de
// identidad en repetir lo que la longitud de la barra ya dice.
export function AgreementsByEntityChart({ byEntity = {}, entityNames = {}, limit = 6 }) {
  const theme = useTheme();

  const rows = useMemo(() => {
    const all = Object.entries(byEntity)
      // El contador va por NIT —la identidad de la entidad, que no cambia—, así
      // que el rótulo se resuelve aquí. Si faltara el nombre se muestra el NIT
      // antes que nada: mejor un número que una barra anónima.
      .map(([nit, value]) => ({ entity: entityNames[nit] || nit, value: value || 0 }))
      .filter((r) => r.value > 0)
      .sort((a, b) => b.value - a.value);
    if (all.length <= limit) return all;
    // La cola se agrupa en "Otras" en vez de alargar la lista: más allá de unas
    // pocas barras el gráfico deja de compararse de un vistazo.
    const head = all.slice(0, limit);
    const tail = all.slice(limit);
    return [...head, { entity: `Otras (${tail.length})`, value: tail.reduce((a, r) => a + r.value, 0) }];
  }, [byEntity, entityNames, limit]);

  const max = Math.max(1, ...rows.map((r) => r.value));

  return (
    <ChartCard title="Convenios por entidad" empty={rows.length === 0}>
      <Stack spacing={1.25} sx={{ mt: 2 }}>
        {rows.map((row) => (
          <Box key={row.entity}>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
              <Typography variant="caption" color="text.secondary" noWrap title={row.entity}>
                {row.entity}
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>{row.value}</Typography>
            </Stack>
            <Box sx={{ height: 8, bgcolor: alpha(theme.palette.primary.main, 0.08), borderRadius: 1 }}>
              <Box
                sx={{
                  height: "100%",
                  width: `${(row.value / max) * 100}%`,
                  bgcolor: theme.palette.primary.main,
                  borderRadius: 1,
                }}
              />
            </Box>
          </Box>
        ))}
      </Stack>
    </ChartCard>
  );
}
